import { useEffect, useMemo, useState } from "react";
import { Button, Col, Row } from "reactstrap";
import { DEFAULT_OPTION, PAGINATION_OPTIONS } from "../../../../utils/Constant";
import SelectBox from "../../../../components/SelectBox";
import { getDistrictsDataAPICall } from "../../../../api/chmActivityAPI";
import { useDispatch, useSelector } from "react-redux";
import { FormUtils, SelectOptionType } from "../../../../utils/FormUtils";
import SearchBox from "../../../../components/SearchBox";
import AddIcon from "@mui/icons-material/Add";
import {
  blocksByDistrictDataAPI,
  districtsByStateDataAPI,
  FilterVendorListAPI,
  icvGetStatesDataAPICall,
} from "../../../../api/icvActivityAPI";
import SendIcon from "@mui/icons-material/Send";
import RestartAltTwoToneIcon from "@mui/icons-material/RestartAltTwoTone";
import ModalComponent from "../../../../components/ModalComponent";
import { VendorCreation } from "./VendorCreation";
import { Controller, set, useForm } from "react-hook-form";
import CPagination from "../../../../components/CPagination";
import ReusableTable, { Column } from "../../../../components/ReusableTable";
import { Chip } from "@mui/material";
import { FilterListPayload } from "../../../../models/VendorModels";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown";
import { CircleSpinnerOverlay } from "react-spinner-overlay";
import { handleICVVendorPaginationDataSlice } from "../../../../redux/slices/globalSlice";
import {
  handleICVFilteredVendorDataSlice,
  handleICVSurveyorDetailsById,
  handleICVVendorDetailsById,
} from "../../../../redux/slices/icvActivitySlice";
import { capitalizeWords } from "../../../../utils/Util";
import { SurveyorCreation } from "./SurveyorCreation";

interface VendorSearchProps {}

export const VendorSearch = ({}: VendorSearchProps) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors, isValid },
  } = useForm<FilterListPayload>({
    defaultValues: {
      vendorCode: "",
      vendorName: "",
      mobileNumber: "",
      email: "",
      stateIds: [],
      districtIds: [],
      blockIds: [],
      isActive: { label: "Active", value: true },
    },
    mode: "all",
  });
  const selectedState = watch("stateIds");
  const selectedDistrict = watch("districtIds");
  const selectedBlock = watch("blockIds");
  const [openModal, setOpenModal] = useState(false);
  const { storeICVVendorPaginationData } = useSelector(
    (state: any) => state.global,
  );
  const { limit, offset } = storeICVVendorPaginationData;
  const [isFormValid, setIsFormValid] = useState(false);
  const [submitHandler, setSubmitHandler] = useState<() => void>(() => {});
  const [mode, setMode] = useState("add");
  const [vendorCode, setVendorCode] = useState("");
  const [openSurveyorModal, setOpenSurveyorModal] = useState(false);
  const [surveyorCode, setSurveyorCode] = useState("");
  const [surveyorSubmitHandler, setSurveyorSubmitHandler] = useState<() => void>(
    () => {},
  );

  const statusDropdownOptions = [
    { label: "Active", value: 1 },
    { label: "Inactive", value: 0 },
  ];

  const dispatch: any = useDispatch();
  const {
    storeICVGetStatesData,
    storeICVDistrictsByStateData,
    storeICVBlocksByDistricData,
    storeFilterVendorList,
    isCrudLoading,
    runListApi,
  } = useSelector((state: any) => state.icvActivity);
  const [vendorData, setVendorData] = useState(storeFilterVendorList);

  const statesData = FormUtils.convertDataList(
    storeICVGetStatesData?.data ?? [],
  );

  // Multi States, Districts and village - Vendor Allocation
  const multiDistrictsData = FormUtils.convertMultiDataDistrictList(
    storeICVDistrictsByStateData?.data ?? [],
  );

  const multiBlocksData = FormUtils.convertMultiBlocksList(
    storeICVBlocksByDistricData?.data ?? [],
  );

  // Effects
  useEffect(() => {
    // Fetching the states initial data on component mount
    dispatch(icvGetStatesDataAPICall());
  }, [dispatch]);

  // Restricting the Districts  when state is removed from the state dropdown
  useEffect(() => {
    if (!multiDistrictsData?.length) return;

    const filteredDistricts: any = selectedDistrict.filter((district: any) => {
      return selectedState.some(
        (state: any) => state.value === district.stateId,
      );
    });

    setValue("districtIds", filteredDistricts);
  }, [selectedState]);

  // Restricting the Tehsil/Blocks when district is removed from district dropdown
  useEffect(() => {
    if (!multiBlocksData?.length) return;

    const filteredBlocks: any = selectedBlock.filter((block: any) => {
      return selectedDistrict.some(
        (district: any) => district.value === block.districtId,
      );
    });

    setValue("blockIds", filteredBlocks);
  }, [selectedDistrict]);

  useEffect(() => {
    if (runListApi) {
      handleSubmit(onSubmit)();
    }
  }, [runListApi]);

  useEffect(() => {
    return () => {
      handleReset();
    };
  }, []);

  // Handlers
  const onchangeDropdown = (label: String, event: any) => {
    switch (label) {
      case "State":
        const stateCodes: string[] = Array.from(
          new Set(event.map((item: SelectOptionType) => item.value)),
        );
        dispatch(districtsByStateDataAPI(stateCodes));

        break;
      case "District":
        const stateCodesfromDistricts: string[] = Array.from(
          new Set(event.map((item: any) => item.stateId)),
        );

        const districtCodes: string[] = Array.from(
          new Set(event.map((item: any) => item.value)),
        );

        dispatch(
          blocksByDistrictDataAPI(stateCodesfromDistricts, districtCodes),
        );

        break;

      default:
        dispatch(getDistrictsDataAPICall(event.value));
        break;
    }
  };

  const toggleOpenModal = () => {
    setOpenModal(!openModal);
    dispatch(handleICVVendorDetailsById([]));
  };

    const toggleSurveyorModal = () => {
      setOpenSurveyorModal(!openSurveyorModal);
       dispatch(handleICVSurveyorDetailsById([]));
    };

  const onSubmit = (
    data: FilterListPayload,
    customLimit?: any,
    customOffset?: any,
  ) => {
    const finalLimit = customLimit ?? limit;
    const finalOffset = customOffset ?? offset;

    dispatch(FilterVendorListAPI(data, finalLimit, finalOffset));
  };

  const handleReset = () => {
    reset();
    dispatch(
      handleICVFilteredVendorDataSlice({
        data: [],
        count: 0,
      }),
    );
  };

  const getUniqueValues = (arr: any[], key: string) => {
    return Array.from(
      new Set(
        arr?.map((a: any) => capitalizeWords(a[key])).filter((val: any) => val),
      ),
    ).join(", ");
  };


  const Vendor_Columns: Column<any>[] = [
    {
      header: "Vendor ID",
      accessor: "vendorCode",
      sticky: "left",
      className: "view-form ",
    },

    {
      header: "Vendor Name",
      accessor: "vendorName",
      cell: (row) => row.vendorName ?? "-",
      tooltip: (row) => row.vendorName,
    },
    {
      header: "Vendor Type",
      accessor: "vendorType",
      cell: (row) =>
        row.vendorType === "VENDOR_ENTERPRISE"
          ? "Vendor Enterprise"
          : "Vendor Individual",
    },

    {
      header: "Email ID",
      accessor: "email",
      cell: (row) => row.email ?? "-",
      tooltip: (row) => row.email,
    },

    {
      header: "Mobile Number",
      accessor: "mobileNumber",
    },

    {
      header: "Status",
      accessor: "isActive",
      // className: "reusable-status-cell",
      cell: (row) => (
        <Chip
          label={row.isActive === true ? "Active" : "Inactive"}
          size="small"
          id={row.isActive === true ? "status-2" : "status-1"}
          // className={row.status === "Active" ? "status-2" : "default"}
          //  color={row.status === "Active" ? "#77ad8e" : "default"}
        />
      ),
    },
    {
      header: "Allocated State",
      className: "reusable-status-cell",
      tooltip: (row) =>
        getUniqueValues(row?.allocations || [], "stateName") || "-",
      cell: (row) =>
        getUniqueValues(row?.allocations || [], "stateName") || "-",
    },
    {
      header: "Allocated District",
      tooltip: (row) =>
        getUniqueValues(row?.allocations || [], "districtName") || "-",
      cell: (row) =>
        getUniqueValues(row?.allocations || [], "districtName") || "-",
    },

    {
      header: "Allocated Tehsil/Block",
      tooltip: (row) =>
        getUniqueValues(row?.allocations || [], "blockName") || "-",
      cell: (row) =>
        getUniqueValues(row?.allocations || [], "blockName") || "-",
    },
  ];

  // Slice the data based on the pagination offset and limit
  const paginatedData = useMemo(() => {
    setVendorData(storeFilterVendorList);
    let filterData = storeFilterVendorList?.data;
    if (filterData) {
      return filterData;
    }
    return [];
  }, [storeFilterVendorList, storeICVVendorPaginationData]);

  const changePaginationLimit = (value: number) => {
    if (vendorData?.data?.length > 0) {
      dispatch(
        handleICVVendorPaginationDataSlice({
          ...storeICVVendorPaginationData,
          offset: 0,
          limit: value,
          selectedLimit: value,
        }),
      );
      handleSubmit((data) => onSubmit(data, value, 0))();
    }
  };

  const prevPage = () => {
    const newOffset =
      storeICVVendorPaginationData.offset -
      storeICVVendorPaginationData.selectedLimit;
    if (newOffset >= 0) {
      const newLimit = storeICVVendorPaginationData.selectedLimit;

      dispatch(
        handleICVVendorPaginationDataSlice({
          ...storeICVVendorPaginationData,
          offset: newOffset,
          limit: newLimit,
        }),
      );
      handleSubmit((data) => onSubmit(data, newLimit, newOffset))();
    }
  };

  const nextPage = () => {
    const count = storeICVVendorPaginationData.selectedLimit;
    if (vendorData.count > 0 && vendorData.count === count) {
      dispatch(
        handleICVVendorPaginationDataSlice({
          ...storeICVVendorPaginationData,
          offset:
            storeICVVendorPaginationData.offset +
            storeICVVendorPaginationData.selectedLimit,
          limit: storeICVVendorPaginationData.selectedLimit,
        }),
      );
      handleSubmit((data) =>
        onSubmit(
          data,
          storeICVVendorPaginationData.selectedLimit,
          storeICVVendorPaginationData.offset +
            storeICVVendorPaginationData.selectedLimit,
        ),
      )();
    }
  };

  return (
    <>
      <>
        <div className="filter-section mb-2">
          {isCrudLoading && (
            <CircleSpinnerOverlay
              loading={isCrudLoading}
              overlayColor="rgba(147, 166, 199, 0.1)"
              color="#5C79AA"
              zIndex={9999}
            />
          )}
          <Row>
            {/* Vendor Name */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="vendorName"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ position: "relative" }}>
                    <SearchBox
                      {...field}
                      label="Vendor Name"
                      placeHolder="Enter Name"
                      maxLength={255}
                      error={!!error}
                      errorMessage={error?.message}
                    />
                  </div>
                )}
              />
            </Col>

            {/* Vendor Id */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="vendorCode"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ position: "relative" }}>
                    <SearchBox
                      {...field}
                      label="Vendor ID"
                      placeHolder="Enter Vendor ID"
                      maxLength={255}
                      error={!!error}
                      errorMessage={error?.message}
                    />
                  </div>
                )}
              />
            </Col>

            {/* Vendor Email */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="email"
                control={control}
                rules={{
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email address",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ position: "relative" }}>
                    <SearchBox
                      {...field}
                      label="Email"
                      placeHolder="Enter Email"
                      error={!!error}
                      errorMessage={error?.message}
                    />
                  </div>
                )}
              />
            </Col>

            {/* Mobile Number */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="mobileNumber"
                control={control}
                rules={{
                  pattern: {
                    value: /^[6-9][0-9]{9}$/,
                    message:
                      "Enter a valid 10‑digit mobile number starting with 6–9",
                  },
                  minLength: {
                    value: 10,
                    message: "mobile number must be 10 digits",
                  },
                  maxLength: {
                    value: 10,
                    message: "mobile number must be 10 digits",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ position: "relative" }}>
                    <SearchBox
                      {...field}
                      label="Mobile Number"
                      placeHolder="Enter Mobile Number"
                      error={!!error}
                      maxLength={10}
                      errorMessage={error?.message}
                      onChange={(e: any) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10);

                        field.onChange(value);
                      }}
                    />
                  </div>
                )}
              />
            </Col>

            {/* Status Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="isActive"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <SelectBox
                    {...field}
                    label="Status"
                    options={statusDropdownOptions}
                    className="css-select-bg"
                  />
                )}
              />
            </Col>

            {/* Multi Selection State Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="stateIds"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <MultiSelectDropdown
                    {...field}
                    fromVendorCreation
                    label="State"
                    options={statesData}
                    onChange={(event: any) => {
                      if (event != null) {
                        setValue("stateIds", event, {
                          shouldValidate: true,
                        });

                        onchangeDropdown("State", event);
                      } else {
                        setValue("stateIds", [DEFAULT_OPTION]);
                      }
                    }}
                  />
                )}
              />
            </Col>

            {/* Multi Selection District Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="districtIds"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <MultiSelectDropdown
                    {...field}
                    fromVendorCreation
                    disabled={Number(selectedState.length) === 0}
                    label="District"
                    options={multiDistrictsData}
                    onChange={(event: any) => {
                      if (event != null) {
                        setValue("districtIds", event, {
                          shouldValidate: true,
                        });
                        onchangeDropdown("District", event);
                      } else {
                        setValue("districtIds", [DEFAULT_OPTION]);
                      }
                    }}
                  />
                )}
              />
            </Col>

            {/* Tehsil/Block Dropdown */}
            <Col xs={12} md={4} lg={3}>
              <Controller
                name="blockIds"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <MultiSelectDropdown
                    {...field}
                    fromVendorCreation
                    disabled={Number(selectedDistrict.length) === 0}
                    label="Tehsil/Block"
                    options={multiBlocksData}
                    onChange={(event: any) => {
                      if (event != null) {
                        setValue("blockIds", event, {
                          shouldValidate: true,
                        });
                      } else {
                        setValue("blockIds", [DEFAULT_OPTION]);
                      }
                    }}
                  />
                )}
              />
            </Col>

            {/* Add Vendor Button */}
            <Col xs={12} md={4} lg={3}>
              <div className="mt-3" style={{ position: "relative" }}>
                <div className="mb-1 text-start search-label text-muted"></div>
                <Button
                  variant="contained"
                  className="w-100 d-flex justify-content-center align-items-center cl-btn-outline-primary"
                  onClick={() => {
                    setOpenModal(true);
                    setMode("add");
                    setVendorCode("");
                    setSurveyorCode('')
                  }}
                >
                  <AddIcon sx={{ fontSize: "18px", marginRight: "5px" }} />
                  Add Vendor
                </Button>
              </div>
            </Col>
            <Col xs={6} md={3} className="d-flex  pt-1 mt-3">
              <Button
                variant="contained"
                className="flex-grow-1  cl-btn-outline-primary  d-flex align-items-center justify-content-center"
                onClick={handleReset}
              >
                Reset
                <RestartAltTwoToneIcon sx={{ ml: 1 }} />
              </Button>
              <Button
                variant="contained"
                className="ms-2 flex-grow-1 cl-btn-primary d-flex align-items-center justify-content-center"
                onClick={handleSubmit((data) => onSubmit(data, 25, 0))}
              >
                Submit
                <SendIcon sx={{ fontSize: "18px", ml: 1 }} />
              </Button>
            </Col>
          </Row>
        </div>
        <div style={{ marginTop: 20, overflowX: "hidden", maxWidth: "100%" }}>
          {paginatedData.length > 0 ? (
            <ReusableTable
              columns={Vendor_Columns}
              data={paginatedData}
              rowKey={(row: any) => row.vendorId}
              onRowClick={(row: any) => {
                row?.surveyorCode ? setOpenSurveyorModal(true) : setOpenModal(true);
                row?.surveyorCode ? setSurveyorCode(row.surveyorCode) : setVendorCode(row.vendorCode);
                setMode("edit");
              }}
            />
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "60px 20px",
                margin: "20px 0",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "14px", fontWeight: "bold" }}>
                  Please apply appropriate filters and submit to view the
                  vendors
                </div>
              </div>
            </div>
          )}
        </div>
        <CPagination
          storeCHMDashboardData={vendorData}
          storeCHMPaginationData={storeICVVendorPaginationData}
          paginationLimitOptions={PAGINATION_OPTIONS.DEFAULT}
          changePaginationLimit={changePaginationLimit}
          nextPage={nextPage}
          prevPage={prevPage}
          showTotalCount={false}
          showHelper={true}
          helperText={`Use the 'Next(≫)' and 'Previous(≪)' buttons to browse through more data.`}
        />
      </>
      {openSurveyorModal && (
        <ModalComponent
          openModal={openSurveyorModal}
          toggleModal={toggleSurveyorModal}
          title={mode === "edit" ? "Edit Vendor Individual" : "Create Vendor Individual"}
          children={
            <SurveyorCreation
              setIsFormValid={setIsFormValid}
              setSubmitFormHandler={setSurveyorSubmitHandler}
              setOpenModal={setOpenSurveyorModal}
              mode={mode}
              surveyorCode={surveyorCode}
            />
          }
          needXlSize
          footer={
            <div className="d-flex justify-content-end">
              <Button
                variant="outline-primary"
                className="mx-2 cl-btn-outline-primary"
                onClick={() => {
                  setOpenSurveyorModal(false);
                  setMode("");
                  setSurveyorCode("");
                  dispatch(handleICVSurveyorDetailsById([]));
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="mx-2 cl-btn-primary"
                disabled={!isFormValid}
                onClick={() => {
                  surveyorSubmitHandler();
                }}
              >
                Submit
              </Button>
            </div>
          }
        />
      )}
      {openModal && (
        <ModalComponent
          openModal={openModal}
          toggleModal={toggleOpenModal}
          title={mode === "edit" ? "Edit Vendor" : "Create Vendor"}
          children={
            <VendorCreation
              setIsFormValid={setIsFormValid}
              setSubmitHandler={setSubmitHandler}
              setOpenModal={setOpenModal}
              mode={mode}
              vendorCode={vendorCode}
            />
          }
          needXlSize
          footer={
            <div className="d-flex justify-content-end">
              <Button
                variant="outline-primary"
                className="mx-2 cl-btn-outline-primary"
                onClick={() => {
                  setOpenModal(false);
                  setMode("");
                  setVendorCode("");
                  dispatch(handleICVVendorDetailsById([]));
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="mx-2 cl-btn-primary"
                disabled={!isFormValid}
                onClick={() => {
                  submitHandler();
                }}
              >
                Submit
              </Button>
            </div>
          }
        />
      )}
    </>
  );
};
